/**
 * User Billing — modal overlay (usage + Utarus-owned plan panel).
 *
 * Agents control plan content via DomainExtension.billing.plans
 * (display names, price strings, intervals, badges, default selection).
 * This surface owns layout, selection UX, and Checkout/Portal actions.
 *
 * Opened from the chat sidebar user footer, or via /billing deep links
 * (Stripe checkout return, bot magic links). Close returns to chat.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SessionUser } from '../types.js';
import { Check, CreditCard, Loader2, X } from 'lucide-react';

interface BillingPageProps {
  session: SessionUser;
  onClose: () => void;
}

interface PaidPlanCard {
  id: string;
  display_name: string;
  caps_summary: { credits: number };
  features: string[];
  price_display: string | null;
  interval: 'month' | 'year' | null;
  description: string | null;
  badge: string | null;
  highlight: boolean;
  sort_order: number;
  is_default: boolean;
}

interface BillingStatus {
  admin?: boolean;
  unlimited?: boolean;
  message?: string;
  entitlement?: {
    plan_id: string;
    status: string;
    source: string;
    display_name: string;
    current_period_end?: string | null;
    cancel_at_period_end?: boolean;
    intro_trial_ends_at?: string | null;
    has_stripe_customer?: boolean;
  };
  usage?: {
    credits: number;
    credit_cap: number | null;
  };
  defaultPaidPlan?: {
    id: string;
    display_name: string;
    caps_summary: { credits: number };
    price_display?: string | null;
    interval?: 'month' | 'year' | null;
    badge?: string | null;
    description?: string | null;
    highlight?: boolean;
  };
  paidPlans?: PaidPlanCard[];
  trialPeriodDays?: number;
  introTrialDays?: number;
  stripeTrialDays?: number;
}

function intervalLabel(interval: 'month' | 'year' | null | undefined): string {
  if (interval === 'year') return '/ year';
  if (interval === 'month') return '/ month';
  return '';
}

function formatWhen(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function statusLabel(status: string | undefined): string | null {
  switch (status) {
    case 'trialing':
      return 'Trial';
    case 'active':
      return 'Active';
    case 'past_due':
      return 'Past due';
    case 'canceled':
      return 'Canceled';
    case 'comped':
      return 'Comped';
    default:
      return null;
  }
}

export function BillingPage({ session, onClose }: BillingPageProps) {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pollHint, setPollHint] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await fetch('/api/billing/status', { credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body.message || body.error || `HTTP ${res.status}`);
    }
    const next = body as BillingStatus;
    setStatus(next);
    setSelectedPlanId((prev) => {
      if (prev && next.paidPlans?.some((p) => p.id === prev)) return prev;
      const def =
        next.paidPlans?.find((p) => p.is_default)?.id ??
        next.defaultPaidPlan?.id ??
        next.paidPlans?.[0]?.id ??
        null;
      return def;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Escape closes the modal
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Success poll after Checkout redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') !== 'success') return;

    setPollHint('Confirming payment…');
    let attempts = 0;
    const max = 15;
    const t = setInterval(() => {
      attempts += 1;
      void load()
        .then(() => {
          setStatus((cur) => {
            if (
              cur?.entitlement &&
              cur.entitlement.plan_id !== 'free' &&
              (cur.entitlement.status === 'active' ||
                cur.entitlement.status === 'trialing' ||
                cur.entitlement.status === 'comped')
            ) {
              setPollHint('Subscription active.');
              clearInterval(t);
              const url = new URL(window.location.href);
              url.searchParams.delete('checkout');
              window.history.replaceState({}, '', url.pathname + url.search);
            }
            return cur;
          });
        })
        .catch(() => {
          /* keep polling */
        });
      if (attempts >= max) {
        clearInterval(t);
        setPollHint(
          'Still waiting for Stripe webhook. Refresh this page in a moment.',
        );
      }
    }, 2000);

    return () => clearInterval(t);
  }, [load]);

  async function startCheckout(planId: string) {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      if (!body.url) throw new Error('Checkout URL missing');
      window.location.href = body.url as string;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  async function openPortal() {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/billing/portal', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      if (!body.url) throw new Error('Portal URL missing');
      window.location.href = body.url as string;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const paidPlans = useMemo(() => {
    const list = status?.paidPlans ?? [];
    // Stable order: sort_order asc, then display name.
    return [...list].sort((a, b) => {
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.display_name.localeCompare(b.display_name);
    });
  }, [status?.paidPlans]);

  const selectedPlan =
    paidPlans.find((p) => p.id === selectedPlanId) ??
    paidPlans.find((p) => p.is_default) ??
    paidPlans[0] ??
    null;

  const source = status?.entitlement?.source;
  const entStatus = status?.entitlement?.status;
  const isBeta = source === 'beta';
  const isAdminComp = source === 'admin_comp' || entStatus === 'comped';
  // Already on a paid Stripe lifecycle (or beta/comp) — no Checkout CTA.
  const hasPaidAccess =
    isBeta ||
    isAdminComp ||
    source === 'stripe' ||
    entStatus === 'active' ||
    entStatus === 'trialing' ||
    entStatus === 'past_due';
  const showUpgrade = !status?.admin && !hasPaidAccess;
  const showPlanCatalog = !status?.admin && paidPlans.length > 0;
  const canManagePortal =
    !status?.admin &&
    !isBeta &&
    (Boolean(status?.entitlement?.has_stripe_customer) || source === 'stripe');
  const cancelScheduled = Boolean(status?.entitlement?.cancel_at_period_end);
  const periodEndLabel = formatWhen(status?.entitlement?.current_period_end);
  const introEndLabel = formatWhen(status?.entitlement?.intro_trial_ends_at);
  const entStatusLabel = statusLabel(status?.entitlement?.status);
  const currentPlanId = status?.entitlement?.plan_id ?? null;

  const used = status?.usage?.credits ?? 0;
  const total = status?.usage?.credit_cap;
  const pct =
    total == null
      ? 0
      : total <= 0
        ? used > 0
          ? 100
          : 0
        : Math.min(100, Math.round((used / total) * 100));

  const trialDays = status?.stripeTrialDays ?? status?.trialPeriodDays ?? 30;

  function planFeaturesLabel(features: string[]): string {
    if (features.length === 0) return '';
    return features.map((f) => f.replace(/_/g, ' ')).join(' · ');
  }

  function planCatalogHint(): string {
    if (showUpgrade) {
      return `${trialDays}-day free trial with card · cancel anytime`;
    }
    if (isBeta) {
      return 'Beta includes full Pro access. Subscription plans are shown for reference.';
    }
    if (isAdminComp) {
      return 'Comped by admin — Checkout is unavailable while the comp is active.';
    }
    if (entStatus === 'past_due') {
      return 'Payment past due — update your card in the Customer Portal.';
    }
    if (hasPaidAccess) {
      return 'Your subscription is managed below. Plan options for reference.';
    }
    return 'Available subscription plans.';
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-dialog-title"
        className="flex max-h-[min(92dvh,800px)] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-stone-200 bg-canvas-elevated shadow-soft"
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-stone-200/80 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <CreditCard className="h-4 w-4 shrink-0 text-blue-600" />
            <h2
              id="billing-dialog-title"
              className="truncate text-sm font-semibold text-stone-900"
            >
              Billing · {session.displayName}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
            title="Close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {error && (
          <div className="shrink-0 border-b border-rose-200 bg-rose-50 px-4 py-2 text-xs text-rose-800">
            {error}
          </div>
        )}
        {pollHint && (
          <div className="shrink-0 border-b border-blue-100 bg-blue-50 px-4 py-2 text-xs text-blue-800">
            {pollHint}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-stone-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-4 px-4 py-4">
              {status?.admin ? (
                <div className="rounded-lg border border-stone-200 bg-white p-4 text-sm text-stone-700">
                  {status.message || 'Admins bypass caps and paywall.'}
                </div>
              ) : (
                <>
                  <section className="rounded-lg border border-stone-200 bg-white p-4">
                    <h3 className="text-sm font-semibold text-stone-900">Current plan</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <p className="text-lg font-medium text-stone-800">
                        {isBeta
                          ? 'Beta'
                          : (status?.entitlement?.display_name ?? 'Free')}
                      </p>
                      {!isBeta && entStatusLabel && (
                        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-medium text-stone-600">
                          {entStatusLabel}
                        </span>
                      )}
                      {cancelScheduled && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
                          Cancels {periodEndLabel ? periodEndLabel : 'at period end'}
                        </span>
                      )}
                    </div>
                    {cancelScheduled && (
                      <p className="mt-2 text-xs leading-relaxed text-amber-900/90">
                        Cancellation is scheduled. You keep Pro access until{' '}
                        <span className="font-semibold">
                          {periodEndLabel ?? 'the end of the current period'}
                        </span>
                        . After that the plan becomes Free.
                      </p>
                    )}
                    {!cancelScheduled &&
                      status?.entitlement?.source === 'intro_trial' &&
                      introEndLabel && (
                        <p className="mt-2 text-xs text-stone-500">
                          Intro trial ends {introEndLabel}
                        </p>
                      )}
                    {!cancelScheduled &&
                      status?.entitlement?.status === 'trialing' &&
                      source === 'stripe' &&
                      periodEndLabel && (
                        <p className="mt-2 text-xs text-stone-500">
                          Stripe trial ends {periodEndLabel}
                        </p>
                      )}

                    <div className="mt-4 space-y-2">
                      <div className="flex items-baseline justify-between text-sm">
                        <span className="text-stone-600">Used</span>
                        <span className="font-semibold text-stone-900">
                          {used.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between text-sm">
                        <span className="text-stone-600">Total</span>
                        <span className="font-semibold text-stone-900">
                          {total == null ? 'unlimited' : total.toLocaleString()}
                        </span>
                      </div>
                      {total != null && (
                        <div className="flex items-baseline justify-between text-sm">
                          <span className="text-stone-600">Used %</span>
                          <span className="font-semibold text-stone-900">{pct}%</span>
                        </div>
                      )}
                      {total != null && total > 0 && (
                        <div className="mt-1 h-2 overflow-hidden rounded-full bg-stone-100">
                          <div
                            className="h-full rounded-full bg-blue-600 transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </section>

                  {showPlanCatalog && (
                    <section className="rounded-lg border border-stone-200 bg-white p-4">
                      <div>
                        <h3 className="text-sm font-semibold text-stone-900">
                          {showUpgrade ? 'Choose a plan' : 'Subscription plans'}
                        </h3>
                        <p className="mt-0.5 text-xs leading-relaxed text-stone-500">
                          {planCatalogHint()}
                        </p>
                      </div>

                      <div
                        className="mt-3 space-y-2"
                        role={showUpgrade ? 'radiogroup' : 'list'}
                        aria-label="Subscription plans"
                      >
                        {paidPlans.map((plan) => {
                          const selected = showUpgrade && selectedPlan?.id === plan.id;
                          const isCurrent =
                            !showUpgrade &&
                            currentPlanId != null &&
                            plan.id === currentPlanId;
                          const featuresLabel = planFeaturesLabel(plan.features);
                          const cardClass = [
                            'w-full rounded-xl border px-3 py-3 text-left transition',
                            selected
                              ? 'border-blue-600 bg-blue-50/60 ring-1 ring-blue-600'
                              : isCurrent
                                ? 'border-emerald-600/70 bg-emerald-50/50 ring-1 ring-emerald-600/40'
                                : plan.highlight
                                  ? 'border-stone-300 bg-white'
                                  : 'border-stone-200 bg-white',
                            showUpgrade && !busy
                              ? 'cursor-pointer hover:border-stone-400'
                              : '',
                            showUpgrade && busy ? 'opacity-60' : '',
                          ].join(' ');

                          const body = (
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className="text-sm font-semibold text-stone-900">
                                    {plan.display_name}
                                  </span>
                                  {plan.badge && (
                                    <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                                      {plan.badge}
                                    </span>
                                  )}
                                  {plan.is_default && !plan.badge && (
                                    <span className="rounded-full bg-stone-100 px-1.5 py-0.5 text-[10px] font-medium text-stone-600">
                                      Recommended
                                    </span>
                                  )}
                                  {isCurrent && (
                                    <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-900">
                                      Current
                                    </span>
                                  )}
                                </div>
                                {plan.description && (
                                  <p className="mt-1 text-xs leading-relaxed text-stone-600">
                                    {plan.description}
                                  </p>
                                )}
                                <p className="mt-1.5 text-xs text-stone-500">
                                  {plan.caps_summary.credits.toLocaleString()} credits
                                  / month
                                  {featuresLabel ? ` · ${featuresLabel}` : ''}
                                </p>
                              </div>
                              <div className="shrink-0 text-right">
                                <div className="text-base font-semibold tabular-nums text-stone-900">
                                  {plan.price_display ?? '—'}
                                </div>
                                <div className="text-[11px] text-stone-500">
                                  {intervalLabel(plan.interval)}
                                </div>
                                {selected && (
                                  <div className="mt-1 inline-flex items-center gap-0.5 text-[11px] font-medium text-blue-700">
                                    <Check className="h-3 w-3" /> Selected
                                  </div>
                                )}
                              </div>
                            </div>
                          );

                          if (showUpgrade) {
                            return (
                              <button
                                key={plan.id}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                disabled={busy}
                                onClick={() => setSelectedPlanId(plan.id)}
                                className={cardClass}
                              >
                                {body}
                              </button>
                            );
                          }

                          return (
                            <div key={plan.id} role="listitem" className={cardClass}>
                              {body}
                            </div>
                          );
                        })}
                      </div>

                      {showUpgrade && (
                        <button
                          type="button"
                          disabled={busy || !selectedPlan}
                          onClick={() => {
                            if (selectedPlan) void startCheckout(selectedPlan.id);
                          }}
                          className="mt-4 w-full rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          {busy
                            ? 'Redirecting…'
                            : selectedPlan
                              ? `Continue with ${selectedPlan.display_name}${
                                  selectedPlan.price_display
                                    ? ` · ${selectedPlan.price_display}`
                                    : ''
                                }`
                              : 'Select a plan'}
                        </button>
                      )}
                    </section>
                  )}

                  {canManagePortal && (
                    <section className="rounded-lg border border-stone-200 bg-white p-4">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void openPortal()}
                          className="rounded-lg bg-stone-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-stone-800 disabled:opacity-50"
                        >
                          {busy ? 'Redirecting…' : 'Manage subscription'}
                        </button>
                        {status?.entitlement?.has_stripe_customer && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void openPortal()}
                            className="rounded-lg border border-stone-200 px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                          >
                            Customer Portal
                          </button>
                        )}
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
